import { ref } from "vue";
import { useHasAllBusesArrivedLastStop } from "./useHasAllBusesArrivedLastStop";
import { useHasAllBusesEndedDriving } from "./useHasAllBusesEndedDriving";
import { IBusLocation } from "@/domain/entities/lineInfo";
import {
  getMinutesSinceLastStopArrival,
  getMinutesUntilDepartureTime,
  hasAnyBusStopEvent,
} from "@/utils/busLocationFetchControl";
import { IBusStop } from "@/domain/entities/busStop";
import {
  ShouldGetBusLocationResult,
  useShouldGetBusLocation,
} from "../map/useShouldGetBusLocation";
import { LineTypeCode } from "@/types";

/** 버스들의 배차상태(AllocStatusCode)와 BusStopEventCode등을 보고 버스의 주행상태를 판단하는 함수 */
export const useBusLocationDrivingStateManager = () => {
  const { shouldGetBusLocationAndFindCampus } = useShouldGetBusLocation();

  const {
    hasAllBusesEndedDriving,
    resetHasAllBusesEndedDriving,
    updateHasAllBusesEndedDriving,
  } = useHasAllBusesEndedDriving();

  const {
    hasAllBusesArrivedLastStop,
    updateHasAllBusesArrivedLastStop,
    resetHasAllBusesArrivedLastStop,
  } = useHasAllBusesArrivedLastStop();

  const isBusRunning = ref(false);
  const updateBusRunningStatus = (
    busLocations: IBusLocation[],
    shouldGetBusLocation: boolean
  ) => {
    isBusRunning.value =
      shouldGetBusLocation && hasAnyBusStopEvent(busLocations);
  };

  const START_TIME_THRESHOLD = 20; // 기점 출발 시각 20분 전
  const shouldGetBusLocation = async (
    isLoggedIn: boolean,
    lastBusStopArrivalThresholdMin: number,
    lineTypeCode: LineTypeCode,
    campusName?: string,
    firstBusStop?: IBusStop,
    lastBusStop?: IBusStop
  ): Promise<ShouldGetBusLocationResult> => {
    // 로그인을 안한 경우에는 버스위치 API 호출 안함
    if (!isLoggedIn) {
      return {
        shouldGetLocation: false,
        locationNotAvailable: false,
        nearbyCampusName: campusName,
      };
    }

    // 사내셔틀인 경우 현재 위치 기반으로 캠퍼스 반경 X미터 이내에 없으면 버스 위치 API 호출 안함
    const result = await shouldGetBusLocationAndFindCampus(
      lineTypeCode,
      campusName
    );
    if (!result.shouldGetLocation) {
      return result;
    }

    // 모든 버스가 운행종료 상태이거나 마지막 정류장에 도착한 상태이면 버스위치 API 호출 안함
    if (hasAllBusesEndedDriving.value || hasAllBusesArrivedLastStop.value) {
      return {
        shouldGetLocation: false,
        locationNotAvailable: false,
        nearbyCampusName: campusName,
      };
    }

    if (!firstBusStop || !lastBusStop) {
      return {
        shouldGetLocation: false,
        locationNotAvailable: false,
        nearbyCampusName: campusName,
      };
    }

    // 현재시간이 기점 출발 시간 20분 전 ~ 종점 정류장 도착시간 X분 후 사이에 있는 경우에만 버스위치 API 호출
    const shouldGetLocation =
      getMinutesSinceLastStopArrival(lastBusStop) >
        -lastBusStopArrivalThresholdMin &&
      getMinutesUntilDepartureTime(firstBusStop) <= START_TIME_THRESHOLD;
    return {
      shouldGetLocation,
      locationNotAvailable: false,
      nearbyCampusName: campusName,
    };
  };

  return {
    hasAllBusesArrivedLastStop,
    updateHasAllBusesArrivedLastStop,
    resetHasAllBusesArrivedLastStop,

    hasAllBusesEndedDriving,
    updateHasAllBusesEndedDriving,
    resetHasAllBusesEndedDriving,

    isBusRunning,
    updateBusRunningStatus,

    shouldGetBusLocation,
  };
};
